/**
 * Plot Builder types — chart type definitions, config schema,
 * engine compatibility matrix, and saved plot model.
 */

// ---------------------------------------------------------------------------
// Chart types
// ---------------------------------------------------------------------------

export type ChartType2D = 'line' | 'scatter' | 'bar' | 'histogram' | 'area' | 'bubble' | 'box' | 'violin' | 'polar' | 'radar';
export type ChartType25D = 'contour' | 'heatmap';
export type ChartType3D = 'surface3d' | 'scatter3d';
export type ChartType = ChartType2D | ChartType25D | ChartType3D;

export const CHART_TYPE_GROUPS = {
  '2D': ['line', 'scatter', 'bar', 'histogram', 'area', 'bubble', 'box', 'violin'] as ChartType[],
  'Polar': ['polar', 'radar'] as ChartType[],
  '2.5D': ['contour', 'heatmap'] as ChartType[],
  '3D': ['surface3d', 'scatter3d'] as ChartType[],
} as const;

export const CHART_TYPE_LABELS: Record<ChartType, string> = {
  line: 'Line', scatter: 'Scatter', bar: 'Bar', histogram: 'Histogram',
  area: 'Area', bubble: 'Bubble', box: 'Box', violin: 'Violin',
  polar: 'Polar', radar: 'Radar', contour: 'Contour', heatmap: 'Heatmap',
  surface3d: '3D Surface', scatter3d: '3D Scatter',
};

export const CHART_TYPE_ICONS: Record<ChartType, string> = {
  line: '📈', scatter: '⚬', bar: '📊', histogram: '▐',
  area: '▓', bubble: '◉', box: '☐', violin: '🎻',
  polar: '◎', radar: '🕸', contour: '◎', heatmap: '🟧',
  surface3d: '🏔', scatter3d: '⬡',
};

// ---------------------------------------------------------------------------
// Engine definitions
// ---------------------------------------------------------------------------

export type PlotEngine = 'plotly' | 'recharts' | 'ag-charts' | 'scichart';

export const ENGINE_LABELS: Record<PlotEngine, string> = {
  plotly: 'Plotly', recharts: 'Recharts', 'ag-charts': 'AG Charts', scichart: 'SciChart',
};

export const ENGINE_COMPATIBILITY: Record<PlotEngine, ChartType[]> = {
  plotly: ['line', 'scatter', 'bar', 'histogram', 'area', 'bubble', 'box', 'violin', 'polar', 'radar', 'contour', 'heatmap', 'surface3d', 'scatter3d'],
  recharts: ['line', 'scatter', 'bar', 'histogram', 'area'],
  'ag-charts': ['line', 'scatter', 'bar', 'histogram', 'area', 'bubble'],
  scichart: ['line', 'scatter', 'bar', 'histogram', 'surface3d', 'scatter3d'],
};

export function getCompatibleEngines(chartType: ChartType): PlotEngine[] {
  return (Object.keys(ENGINE_COMPATIBILITY) as PlotEngine[]).filter((eng) =>
    ENGINE_COMPATIBILITY[eng].includes(chartType),
  );
}

export function getDefaultEngine(chartType: ChartType): PlotEngine {
  if (['contour', 'heatmap', 'surface3d', 'scatter3d', 'box', 'violin', 'polar', 'radar', 'bubble'].includes(chartType)) return 'plotly';
  return 'recharts';
}

// ---------------------------------------------------------------------------
// Axis / scale config
// ---------------------------------------------------------------------------

export type AxisScale = 'linear' | 'log' | 'sqrt' | 'reversed';

export interface AxisConfig {
  field: string;
  scale: AxisScale;
  label?: string;
  min?: number;
  max?: number;
  tickCount?: number;
  tickFormat?: string;
}

// ---------------------------------------------------------------------------
// Color / style primitives
// ---------------------------------------------------------------------------

export type MarkerShape = 'circle' | 'square' | 'diamond' | 'triangle' | 'cross' | 'x';
export type LineStyle = 'solid' | 'dashed' | 'dotted' | 'dashdot';
export type BarMode = 'group' | 'stack' | 'overlay' | 'relative';
export type LegendPosition = 'top' | 'bottom' | 'left' | 'right' | 'none';
export type ColorScale = 'Viridis' | 'Plasma' | 'Inferno' | 'Magma' | 'Cividis'
  | 'YlOrRd' | 'YlGnBu' | 'RdBu' | 'Spectral' | 'Turbo' | 'Hot' | 'Jet'
  | 'Greens' | 'Blues' | 'Portland' | 'Electric';
export type TrendLineType = 'none' | 'linear' | 'polynomial' | 'exponential' | 'loess';
export type FontWeight = 'normal' | 'bold' | 'light';

export const COLOR_SCALE_OPTIONS: ColorScale[] = [
  'Viridis', 'Plasma', 'Inferno', 'Magma', 'Cividis',
  'YlOrRd', 'YlGnBu', 'RdBu', 'Spectral', 'Turbo',
  'Hot', 'Jet', 'Greens', 'Blues', 'Portland', 'Electric',
];

export const MARKER_SHAPE_OPTIONS: MarkerShape[] = ['circle', 'square', 'diamond', 'triangle', 'cross', 'x'];
export const LINE_STYLE_OPTIONS: LineStyle[] = ['solid', 'dashed', 'dotted', 'dashdot'];
export const BAR_MODE_OPTIONS: BarMode[] = ['group', 'stack', 'overlay', 'relative'];
export const LEGEND_POSITION_OPTIONS: LegendPosition[] = ['top', 'bottom', 'left', 'right', 'none'];
export const TREND_LINE_OPTIONS: TrendLineType[] = ['none', 'linear', 'polynomial', 'exponential', 'loess'];
export const FONT_WEIGHT_OPTIONS: FontWeight[] = ['normal', 'bold', 'light'];

export const PALETTES: Record<string, string[]> = {
  'FlexCompute':  ['#00643C', '#6DB5DD', '#F78B8D', '#FFC53D', '#7C66DC', '#E5484D', '#30A46C', '#3E63DD'],
  'Viridis':      ['#440154', '#482878', '#3E4989', '#31688E', '#26828E', '#1F9E89', '#6DCD59', '#FDE725'],
  'Pastel':       ['#F28E8E', '#F2C28E', '#F2F28E', '#8EF2A0', '#8EC2F2', '#B28EF2', '#F28EDA', '#8EF2E0'],
  'Bold':         ['#E63946', '#457B9D', '#1D3557', '#2A9D8F', '#E9C46A', '#F4A261', '#264653', '#A8DADC'],
  'Neon':         ['#FF006E', '#FB5607', '#FFBE0B', '#8338EC', '#3A86FF', '#06D6A0', '#EF476F', '#118AB2'],
  'Earth':        ['#6B4226', '#D4A574', '#A67B5B', '#8B9556', '#4A7C59', '#556B2F', '#8B7355', '#CD853F'],
  'Monochrome':   ['#111111', '#333333', '#555555', '#777777', '#999999', '#BBBBBB', '#DDDDDD', '#EEEEEE'],
};

// ---------------------------------------------------------------------------
// Visual mapping
// ---------------------------------------------------------------------------

export type MappingMode = 'fixed' | 'byVariable';

export interface VisualMapping {
  mode: MappingMode;
  field?: string;
  bin: boolean;
  binCount: number;
}

export function createDefaultMapping(): VisualMapping {
  return { mode: 'fixed', bin: false, binCount: 5 };
}

// ---------------------------------------------------------------------------
// Plot builder config
// ---------------------------------------------------------------------------

export type DataSourceMode = 'full' | 'filtered';

export interface PlotBuilderConfig {
  chartType: ChartType;
  engine: PlotEngine;
  dataSource: DataSourceMode;
  xAxis: AxisConfig;
  yAxis: AxisConfig;
  zAxis?: AxisConfig;
  groupBy?: string;
  colorField?: string;
  sizeField?: string;
  colorMapping: VisualMapping;
  sizeMapping: VisualMapping;
  shapeMapping: VisualMapping;
  title: string;
  titleSize: number;
  titleWeight: FontWeight;
  showGrid: boolean;
  showLegend: boolean;
  legendPosition: LegendPosition;
  markerSize: number;
  markerShape: MarkerShape;
  markerOpacity: number;
  lineStyle: LineStyle;
  lineWidth: number;
  barMode: BarMode;
  barGap: number;
  barOpacity: number;
  palette: string;
  colorScale: ColorScale;
  invertColorScale: boolean;
  trendLine: TrendLineType;
  trendLineOrder: number;
  showMean: boolean;
  showMedian: boolean;
  showErrorBars: boolean;
  showDataLabels: boolean;
  /** For CL–α (or any Y vs angle): show linear-fit slope of the linear part (e.g. "Lift curve slope = 0.12 /deg") */
  showLiftCurveSlope: boolean;
  plotPadding: number;
  aspectRatio: 'auto' | '16:9' | '4:3' | '1:1' | '3:4';
  dpi: number;
  histBins: number;
  contourLevels: number;
  contourSmooth: boolean;
  xAxisTickAngle: number;
  yAxisTickAngle: number;
}

export function createDefaultConfig(): PlotBuilderConfig {
  return {
    chartType: 'scatter',
    engine: 'recharts',
    dataSource: 'full',
    xAxis: { field: '', scale: 'linear' },
    yAxis: { field: '', scale: 'linear' },
    groupBy: undefined,
    colorMapping: createDefaultMapping(),
    sizeMapping: createDefaultMapping(),
    shapeMapping: createDefaultMapping(),
    title: '',
    titleSize: 14,
    titleWeight: 'bold',
    showGrid: true,
    showLegend: true,
    legendPosition: 'right',
    markerSize: 6,
    markerShape: 'circle',
    markerOpacity: 0.85,
    lineStyle: 'solid',
    lineWidth: 2,
    barMode: 'group',
    barGap: 0.2,
    barOpacity: 0.85,
    palette: 'FlexCompute',
    colorScale: 'Viridis',
    invertColorScale: false,
    trendLine: 'none',
    trendLineOrder: 2,
    showMean: false,
    showMedian: false,
    showErrorBars: false,
    showDataLabels: false,
    showLiftCurveSlope: false,
    plotPadding: 40,
    aspectRatio: 'auto',
    dpi: 150,
    histBins: 20,
    contourLevels: 10,
    contourSmooth: true,
    xAxisTickAngle: 0,
    yAxisTickAngle: 0,
  };
}

// ---------------------------------------------------------------------------
// Saved plot
// ---------------------------------------------------------------------------

export const PLOT_COLLECTION_KIND = 'plot_collection' as const;

export type PlotCollectionKind = typeof PLOT_COLLECTION_KIND;
export type PlotCollectionLayoutMode = 'grid';
export type PlotCollectionColumnCount = 1 | 2 | 3;

export interface PlotCollectionLayout {
  mode: PlotCollectionLayoutMode;
  columns: PlotCollectionColumnCount;
}

export interface PlotCollectionItemRef {
  plotId?: string;
  path?: string;
  title?: string;
  chartType?: string;
  position?: {
    row?: number;
    col?: number;
    rowSpan?: number;
    colSpan?: number;
  };
}

export interface PlotCollectionConfig {
  kind: PlotCollectionKind;
  version: 1;
  title: string;
  layout: PlotCollectionLayout;
  items: PlotCollectionItemRef[];
}

export function isPlotCollectionConfig(value: unknown): value is PlotCollectionConfig {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  if (record.kind !== PLOT_COLLECTION_KIND) return false;
  if (!record.layout || typeof record.layout !== 'object') return false;
  if (!Array.isArray(record.items)) return false;

  const layout = record.layout as Record<string, unknown>;
  return (
    layout.mode === 'grid' &&
    [1, 2, 3].includes(Number(layout.columns)) &&
    record.items.every((item) => {
      if (!item || typeof item !== 'object') return false;
      const itemRecord = item as Record<string, unknown>;
      return typeof itemRecord.plotId === 'string' || typeof itemRecord.path === 'string';
    })
  );
}

export function createPlotCollectionConfig(
  title: string,
  plotIds: string[],
  columns: PlotCollectionColumnCount = 2,
): PlotCollectionConfig {
  return {
    kind: PLOT_COLLECTION_KIND,
    version: 1,
    title,
    layout: {
      mode: 'grid',
      columns,
    },
    items: plotIds.map((plotId) => ({ plotId })),
  };
}

export interface SavedPlotData {
  id: string;
  project_id: string;
  title: string;
  chart_type: ChartType | PlotCollectionKind;
  config: PlotBuilderConfig | PlotCollectionConfig;
  data_snapshot: Record<string, unknown>[];
  created_at: string;
  updated_at: string | null;
}

// ---------------------------------------------------------------------------
// Renderer props
// ---------------------------------------------------------------------------

export interface ChartRendererProps {
  data: Record<string, unknown>[];
  config: PlotBuilderConfig;
  theme: 'light' | 'dark';
  width?: number;
  height?: number;
  /** When true, minimal chrome (no axis labels, tight margins) for DAG thumbnails */
  mini?: boolean;
  onPointClick?: (row: Record<string, unknown>) => void;
  selectedNodeId?: string | null;
}
